import { ReferentPriceTypes } from '@strata/constants';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { DateInputFormik } from '@strata/react-date-time';
import { FileUploadFormik } from '@strata/react-file-upload';
import { FormContainer } from '@strata/react-form-container';
import { InlineMessage } from '@strata/react-inline-message';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';

import { handleError } from 'services/ErrorManagement';
import { postFile, useGet } from 'services/Http';

export const PriceListUpload = () => {
    const navigate = useNavigate();

    const goHome = () => navigate('/price-list/');

    const [uploadError, setUploadError] = useState();

    const priceSources = useGet('/reference/prices/sources');
    const priceSourceOptions = (priceSources ?? []).map((source) => ({
        text: source.display_name,
        value: source.price_source,
        isStandard: source.is_standard,
        disabled: !source.can_manually_upload,
    }));
    return (
        <Formik
            initialValues={{}}
            enableReinitialize
            validationSchema={Yup.object({
                sourceType: Yup.string().required(validationStrings.requiredField.errorMessage),
                source: Yup.string().required(validationStrings.requiredField.errorMessage),
                referent: Yup.string().required(validationStrings.requiredField.errorMessage),
                effectiveDate: DateInputFormik.validator()
                    .required(validationStrings.requiredField.errorMessage)
                    .min(dayjs().add(1, 'day').startOf('day').toDate(), 'Must be future date'),
                priceListFile: Yup.array()
                    .required(validationStrings.requiredField.errorMessage)
                    .min(1, validationStrings.requiredField.errorMessage),
            })}
            onSubmit={async (values) => {
                try {
                    setUploadError();
                    await postFile(
                        `/reference/prices/pricelist?referent_price_type=${
                            values.referent
                        }&price_source=${values.source}&price_date=${formatDate(
                            values.effectiveDate,
                            DateFormats.HYPHEN_DATE,
                        )}`,
                        values.fileToUpload,
                    );

                    SuccessToast({ message: 'Price list uploaded successfully.' });
                    goHome();
                } catch (err) {
                    const uploadErr = JSON.parse(err.message ?? {})?.detail;
                    if (uploadErr) {
                        setUploadError(uploadErr);
                        handleError('Error uploading file.', err);
                    } else {
                        handleError('Unhandled error', err);
                    }
                }
            }}
        >
            {(formik) => (
                <FormContainer
                    isLoading={!priceSources}
                    formTitle="Price List"
                    primaryButtonLabel="Upload"
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                    onSecondaryButtonClick={goHome}
                    formLeftContent={
                        <>
                            <RadioButtonGroupFormik
                                name="sourceType"
                                label="Source Type"
                                required
                                options={['Health Plan', 'Standard']}
                                onChange={(newVal) =>
                                    formik.setValues({
                                        ...formik.values,
                                        sourceType: newVal,
                                        source: undefined,
                                    })
                                }
                            />
                            {formik.values.sourceType && (
                                <RadioButtonGroupFormik
                                    name="source"
                                    label="Source"
                                    required
                                    options={priceSourceOptions?.filter(
                                        (source) =>
                                            source.isStandard ===
                                            (formik.values.sourceType === 'Standard'),
                                    )}
                                />
                            )}
                            <RadioButtonGroupFormik
                                name="referent"
                                label="Referent"
                                required
                                options={[ReferentPriceTypes.ASP, ReferentPriceTypes.AWP]}
                            />
                            <DateInputFormik
                                id="effectiveDate"
                                label="Effective Date"
                                required
                                helperText="The earliest selectable date is tomorrow."
                                minDate={dayjs().add(1, 'day').startOf('day').toDate()}
                            />
                            {uploadError && (
                                <div className="margin__bottom--medium">
                                    <InlineMessage
                                        intent={InlineMessage.Intent.ERROR}
                                        title="Error Uploading File"
                                        label={
                                            <div
                                                className="flex__gap--small"
                                                style={{ flexDirection: 'column' }}
                                            >
                                                {uploadError.map((err) => (
                                                    <div key={err.error_message}>
                                                        {err.error_message}
                                                        <ul style={{ margin: 0 }}>
                                                            {err.invalid_rows?.map((row) => (
                                                                <li
                                                                    key={row.row_number}
                                                                >{`Row ${row.row_number} - ${row.error}`}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                ))}
                                            </div>
                                        }
                                    />
                                </div>
                            )}
                            <FileUploadFormik
                                id="priceListFile"
                                label="Price List File"
                                required
                                accept=".csv"
                                maxAllowedFiles={1}
                                onAddFiles={(files) =>
                                    formik.setFieldValue('fileToUpload', files?.[0])
                                }
                                onRemoveFile={() => formik.setFieldValue('fileToUpload', undefined)}
                            />
                        </>
                    }
                />
            )}
        </Formik>
    );
};
